import { useAITranslate } from "@/hooks/useAITranslate";
import { useL } from "@/lib/useL";
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageSEO from "@/components/PageSEO";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, List } from "lucide-react";
import DOMPurify from "dompurify";
import ArticleAuthorHeader from "@/components/ArticleAuthorHeader";

function slugifyHeading(s: string) {
  return s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 80);
}

function processArticleHtml(raw: string): { html: string; toc: { id: string; text: string; level: 2 | 3 }[] } {
  if (typeof window === "undefined") return { html: raw, toc: [] };
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${raw}</div>`, "text/html");
  // Remove any leading H1 — we render it ourselves at the top
  doc.querySelectorAll("h1").forEach((el) => el.remove());
  const toc: { id: string; text: string; level: 2 | 3 }[] = [];
  const seen = new Set<string>();
  doc.querySelectorAll("h2, h3").forEach((el) => {
    const text = (el.textContent || "").trim();
    if (!text) return;
    let id = slugifyHeading(text) || `section-${toc.length + 1}`;
    let suffix = 1;
    while (seen.has(id)) { id = `${slugifyHeading(text)}-${++suffix}`; }
    seen.add(id);
    el.setAttribute("id", id);
    toc.push({ id, text, level: el.tagName === "H2" ? 2 : 3 });
  });
  return { html: doc.body.firstElementChild?.innerHTML || raw, toc };
}

const BlogArticle = () => {
  const { slug } = useParams<{ slug: string }>();
  const L = useL();
  const [article, setArticle] = useState<any>(null);
  const [images, setImages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    (async () => {
      const { data: a } = await (supabase as any).from("blog_articles").select("*").eq("slug", slug).eq("status", "published").maybeSingle();
      setArticle(a);
      if (a) {
        const { data: imgs } = await (supabase as any).from("blog_article_images").select("*").eq("article_id", a.id).order("slot");
        setImages(imgs || []);
      }
      setLoading(false);
    })();
  }, [slug]);

  // Old French articles are shown as written (no translation); English ones can be read in French.
  const isOld = (article?.lang || "fr") === "fr";
  const [tH1, tBody] = useAITranslate(isOld ? [""] : [article?.h1 || article?.title || "", article?.body_html || ""], "en") as string[];

  if (loading) return <main className="min-h-screen"><Navbar /><div className="pt-28 text-center">{L('Loading…', 'Chargement…')}</div></main>;
  if (!article) return (
    <main className="min-h-screen bg-background">
      <PageSEO
        title="Article introuvable | Maroc Transfers"
        description="Cet article du blog Maroc Transfers est introuvable. Retournez au guide pour découvrir nos autres articles, conseils voyage et excursions au Maroc."
        path={`/guide/blog/${slug}`}
        noindex
      />
      <Navbar />
      <div className="container mx-auto px-6 py-24 text-center">
        <h1 className="font-heading text-3xl font-bold mb-4">{L('Article not found', 'Article introuvable')}</h1>
        <Link to="/guide" className="text-primary hover:underline"><ArrowLeft className="inline h-4 w-4 mr-1" />Retour au guide</Link>
      </div>
      <Footer />
    </main>
  );

  const sanitized = DOMPurify.sanitize(tBody || article.body_html || "", {
    ALLOWED_TAGS: [
      "h1","h2","h3","h4","h5","h6","p","br","hr",
      "ul","ol","li","blockquote","pre","code",
      "strong","em","b","i","u","s","span","div",
      "a","img","figure","figcaption","aside",
      "table","thead","tbody","tr","th","td",
    ],
    ALLOWED_ATTR: ["href","title","target","rel","src","alt","width","height","class","id"],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  });
  const { html: bodyHtml, toc } = processArticleHtml(sanitized);
  const heroImage = images[0];
  const wordCount = (article.body_html || "").replace(/<[^>]+>/g, " ").split(/\s+/).filter(Boolean).length;
  const readingMinutes = Math.max(1, Math.round(wordCount / 200));

  return (
    <main className="min-h-screen bg-background">
      <PageSEO title={`${article.title} | Maroc Transfers`} description={article.meta_description} path={`/guide/blog/${article.slug}`} ogType="article" />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "Article",
          headline: article.h1 || article.title, description: article.meta_description,
          image: images.map((i) => i.image_url),
          datePublished: article.published_at,
          dateModified: article.updated_at,
          url: `https://maroctransfers.com/guide/blog/${article.slug}`,
          author: { "@type": "Organization", name: "Maroc Transfers" },
          publisher: { "@type": "Organization", name: "Maroc Transfers" },
        })}</script>
      </Helmet>
      <Navbar />
      <article className="pt-28 pb-20">
        <div className="mx-auto max-w-[720px] px-5 md:px-6">
          <Link to="/guide" className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1 mb-6">
            <ArrowLeft className="h-4 w-4" /> {L('Back to guide', 'Retour au guide')}
          </Link>
          <div className="article-prose">
            <h1>{tH1 || article.h1 || article.title}</h1>
          </div>
          <div className="mt-3">
            <ArticleAuthorHeader authorId={article.author_id} publishedAt={article.published_at} readingMinutes={readingMinutes} />
          </div>
          <div className="text-xs text-muted-foreground mt-2 mb-6 flex flex-wrap gap-x-4 gap-y-1">
            {article.published_at && (
              <span>
                {L("Published", "Publié le")} <time dateTime={article.published_at}>{new Date(article.published_at).toLocaleDateString(L("en-GB", "fr-FR"), { year: "numeric", month: "long", day: "numeric" })}</time>
              </span>
            )}
            {article.updated_at && article.updated_at !== article.published_at && (
              <span>
                · {L("Updated", "Mis à jour le")} <time dateTime={article.updated_at}>{new Date(article.updated_at).toLocaleDateString(L("en-GB", "fr-FR"), { year: "numeric", month: "long", day: "numeric" })}</time>
              </span>
            )}
          </div>
          {heroImage && (
            <figure className="mb-6 mt-2">
              <img
                src={heroImage.image_url}
                alt={heroImage.alt_text || article.title}
                title={heroImage.title || article.title}
                className="w-full h-64 sm:h-96 object-cover rounded-xl"
                loading="eager"
              />
              {heroImage.description && (
                <figcaption className="sr-only">{heroImage.description}</figcaption>
              )}
            </figure>
          )}
          {toc.length >= 2 && (
            <nav aria-label="Contents" className="mb-10 rounded-2xl border border-border bg-muted/40 p-5">
              <p className="font-heading flex items-center gap-2 text-base font-semibold text-foreground mb-3">
                <List className="h-4 w-4 text-primary" /> {L('Contents', 'Sommaire')}
              </p>
              <ol className="space-y-1.5 text-sm">
                {toc.map((t, i) => (
                  <li key={t.id} className={t.level === 3 ? "pl-5" : ""}>
                    <a href={`#${t.id}`} className="text-foreground/80 hover:text-primary hover:underline">
                      {t.level === 2 ? `${i + 1}. ` : ""}{t.text}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          )}
          <div
            className="article-prose"
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
          />
        </div>
      </article>
      <Footer />
    </main>
  );
};

export default BlogArticle;